import type React from 'react';

import {
  AdornedInput,
  Divider,
  FieldSet,
  FormField,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
} from '@zillow/constellation-10';

import { TELEPHONE_TV_INTERNET } from '../../../../constants';
import type { Section, SnowRemovalSection } from '../../../../models';
import InlineTooltip from '../../../../../shared/components/Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (object) => void;
  setSection: (section: SnowRemovalSection) => void;
};

export default function TelephoneTVInternet({ section, setSectionValues }: Props) {
  function onChargesChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;
    const { fieldName } = event.currentTarget.dataset;

    setSectionValues({ [fieldName]: value });
  }

  function onChargesClick(
    landlordSetUpMonthly: string,
    landlordSetUpAndPay: string,
    tenantSetUpAndPay: string,
    notProvided: string,
  ) {
    setSectionValues({
      [landlordSetUpMonthly]: true,
      [landlordSetUpAndPay]: false,
      [tenantSetUpAndPay]: false,
      [notProvided]: false,
    });
  }

  function onChargeTypeClick(
    event: React.ChangeEvent<HTMLInputElement>,
    fields: {
      landlordSetUpAndPay: string;
      landlordSetUpMonthly: string;
      tenantSetUpAndPay: string;
      notProvided: string;
      landlordMonthlyCharge: string;
    },
  ): void {
    const { id } = event.currentTarget;

    const { landlordSetUpAndPay, landlordSetUpMonthly, tenantSetUpAndPay, notProvided, landlordMonthlyCharge } = fields;

    setSectionValues({
      [landlordSetUpAndPay]: id === landlordSetUpAndPay,
      [landlordSetUpMonthly]: id === landlordSetUpMonthly,
      [tenantSetUpAndPay]: id === tenantSetUpAndPay,
      [notProvided]: id === notProvided,
      [landlordMonthlyCharge]: '',
    });
  }

  function renderUtilityButtons() {
    const utilityList = [
      {
        name: 'telephone',
        fields: {
          landlordSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.TELEPHONE_LANDLORD_SET_UP_AND_PAY,
          landlordSetUpMonthly: TELEPHONE_TV_INTERNET.FIELD_NAME.TELEPHONE_LANDLORD_SET_UP_AND_MONTHLY_CHARGE,
          landlordMonthlyCharge: TELEPHONE_TV_INTERNET.FIELD_NAME.TELEPHONE_LANDLORD_MONTHLY_CHARGE,
          tenantSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.TELEPHONE_TENANT_SET_UP_AND_PAY,
          notProvided: TELEPHONE_TV_INTERNET.FIELD_NAME.TELEPHONE_NOT_PROVIDED,
        },
      },
      {
        name: 'cable TV',
        fields: {
          landlordSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.CABLETV_LANDLORD_SET_UP_AND_PAY,
          landlordSetUpMonthly: TELEPHONE_TV_INTERNET.FIELD_NAME.CABLETV_LANDLORD_SET_UP_AND_MONTHLY_CHARGE,
          landlordMonthlyCharge: TELEPHONE_TV_INTERNET.FIELD_NAME.CABLETV_LANDLORD_MONTHLY_CHARGE,
          tenantSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.CABLETV_TENANT_SET_UP_AND_PAY,
          notProvided: TELEPHONE_TV_INTERNET.FIELD_NAME.CABLETV_NOT_PROVIDED,
        },
      },
      {
        name: 'internet',
        fields: {
          landlordSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.INTERNET_LANDLORD_SET_UP_AND_PAY,
          landlordSetUpMonthly: TELEPHONE_TV_INTERNET.FIELD_NAME.INTERNET_LANDLORD_SET_UP_AND_MONTHLY_CHARGE,
          landlordMonthlyCharge: TELEPHONE_TV_INTERNET.FIELD_NAME.INTERNET_LANDLORD_MONTHLY_CHARGE,
          tenantSetUpAndPay: TELEPHONE_TV_INTERNET.FIELD_NAME.INTERNET_TENANT_SET_UP_AND_PAY,
          notProvided: TELEPHONE_TV_INTERNET.FIELD_NAME.INTERNET_NOT_PROVIDED,
        },
      },
    ];

    return utilityList.map((utility, index) => {
      const { landlordSetUpAndPay, landlordSetUpMonthly, tenantSetUpAndPay, notProvided, landlordMonthlyCharge } =
        utility.fields;

      return (
        <div key={utility.name}>
          {index !== utilityList.length && index !== 0 && (
            <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          )}
          <FieldSet required legend={<Legend>{`Who will set up and pay for ${utility.name}?`}</Legend>}>
            <LabeledControl
              controlId={landlordSetUpAndPay}
              label={<Label>Landlord will set up and and cost will be part of base rent</Label>}
              control={
                <Radio
                  name={`utility-option-${utility.name}`}
                  checked={section.getFieldValue(landlordSetUpAndPay)}
                  onChange={(e) => onChargeTypeClick(e, utility.fields)}
                />
              }
            />
            <LabeledControl
              controlId={landlordSetUpMonthly}
              label={
                <Label>
                  Landlord will set up and charge a{' '}
                  <InlineTooltip tooltipBodyText="The cost should be based upon Landlords reasonable and good faith estimate of the amount that will reimburse Landlord for the cost of providing this service to the Property.">
                    fixed monthly charge
                  </InlineTooltip>
                </Label>
              }
              control={
                <Radio
                  name={`utility-option-${utility.name}`}
                  checked={section.getFieldValue(landlordSetUpMonthly)}
                  onChange={(e) => onChargeTypeClick(e, utility.fields)}
                />
              }
            />
            <FormField
              required
              css={{ marginLeft: 'lg' }}
              control={
                <AdornedInput.Root>
                  <AdornedInput.Adornment>$</AdornedInput.Adornment>
                  <AdornedInput.Input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Monthly fee for ${utility.name}`}
                    data-field-name={landlordMonthlyCharge}
                    name={landlordMonthlyCharge}
                    value={section.getFieldValue(landlordMonthlyCharge)}
                    onChange={onChargesChange}
                    onBlur={onChargesChange}
                    onFocus={() =>
                      onChargesClick(landlordSetUpMonthly, landlordSetUpAndPay, tenantSetUpAndPay, notProvided)
                    }
                  />
                  <AdornedInput.Adornment>per month</AdornedInput.Adornment>
                </AdornedInput.Root>
              }
            />
            <LabeledControl
              controlId={tenantSetUpAndPay}
              label={<Label>Tenant will set up and pay</Label>}
              control={
                <Radio
                  name={`utility-option-${utility.name}`}
                  checked={section.getFieldValue(tenantSetUpAndPay)}
                  onChange={(e) => onChargeTypeClick(e, utility.fields)}
                />
              }
            />
            <LabeledControl
              controlId={notProvided}
              label={<Label>Not applicable</Label>}
              control={
                <Radio
                  name={`utility-option-${utility.name}`}
                  checked={section.getFieldValue(notProvided)}
                  onChange={(e) => onChargeTypeClick(e, utility.fields)}
                />
              }
            />
          </FieldSet>
        </div>
      );
    });
  }

  return (
    <>
      <Paragraph textStyle="body-lg-bold">Telephone/cable TV/internet</Paragraph>
      {renderUtilityButtons()}
    </>
  );
}
